import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Moment from 'react-moment';
import * as Yup from 'yup';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import ConfirmInput from '../../../components/confirm-input';
import ConfirmSelect from '../../../components/confirm-select';
import SaveBackdrop from '../../../components/saveBackdrop';
import Screen from '../../../components/screen';
import SplashWait from '../../../components/splash-wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadKit, canReadProgram, canWriteKit, canWriteProgram,
  compareArrayObjectValue, getBreadcrumbMaxItems, getErrorObject, getKitDetail, goToRoutePage,
  isNullOrWhiteSpace, redirectRoutePage, setWindowTitle
} from '../../../utils/helper';
import { AddIcon, ChevronRightIcon, DeleteIcon, LinkIcon } from '../../../utils/icons';
import {
  Alert, Box, Breadcrumbs, Button, Card, CardContent, CardHeader, FormControl, FormControlLabel,
  FormGroup, FormHelperText, FormLabel, Grid, IconButton, InputLabel, Link, List, ListItem,
  ListItemSecondaryAction, ListItemText, Paper, Select, Switch, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';
import KitItemCRUD from '../crud/kititem-crud';
import KitItemList from '../kit-item-list';

// Default form value
const formDefaultValue = {
  active: false,
  committmentPeriod: 0,
  fulfillmentVendorKey: "",
  fulfillmentVendorName: "",
  hsbKitNo: 0,
  kitItemCount: 0,
  kitKey: "",
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  name: "",
  platformVendorKey: "",
  platformVendorName: "",
  programCodes: "",
  programCount: 0,
  rechargeDiscountCode: "",
  replacementKit: false,
  subscriptionBilling: false,
  enrollmentCount: 0,
  defaultInstallMethodId:"" 
};

const formDefaultTouchValue = {
  active: true,
  committmentPeriod: true,
  fulfillmentVendorKey: true,
  fulfillmentVendorName: false,
  hsbKitNo: true,
  kitItemCount: false,
  kitKey: true,
  name: true,
  platformVendorKey: true,
  platformVendorName: false,
  programCodes: false,
  programCount: false,
  rechargeDiscountCode: true,
  replacementKit: true,
  subscriptionBilling: true,
  defaultInstallMethodId :true
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required."),
  committmentPeriod: Yup.number().when(["subscriptionBilling"], {
    is: (subscriptionBilling) => {
      return subscriptionBilling;
    },
    then: Yup.number().required("Committment period is required."),
  }),
  fulfillmentVendorKey: Yup.string().required("Fulfillment vendor is required"),
  platformVendorKey: Yup.string().required("Platform vendor is required"),
  replacementKit: Yup.boolean().required("Replacement kit is required."),
  subscriptionBilling: Yup.boolean().required("Subscription billing is required."),
  active: Yup.boolean().required("Status is required"),
  defaultInstallMethodId: Yup.string().trim().required("Install option is required.")
});

const KitCRUD: React.FC = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { setOnboardingPagePathName, refreshData, referenceLookups, showAlert, userFunctionAccess } = useContext(AppContext);

  const { kitKey, kitItemKey } = useParams();

  const [associatedProgramLookupValues, setAssociatedProgramLookupValues] = useState<Lookup[]>([]);
  const [associatedProgramDialogOpen, setAssociatedProgramDialogOpen] = useState<boolean>(false);

  const [cloneKitConfirmDialogInputOpen, setCloneKitConfirmDialogInputOpen] = useState<boolean>(false);
  const [deleteConfirmDialogOpenForKit, setDeleteConfirmDialogOpenForKit] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<Kit>(formDefaultValue);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [limitedUpdates, setLimitedUpdates] = useState<boolean>(false);
  const [isPageInitialize, setIsPageInitialize] = useState<boolean>(false);
  const [isSubscriptionBilling, setIsSubscriptionBilling] = useState<boolean>(false);
  const [kitAssociations, setKitAssociations] = useState<KitAssociation[]>([]);
  const [kitItemDialogOpen, setKitItemDialogOpen] = useState<boolean>(false);
  const [kitItems, setKitItems] = useState<KitItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [selectedDeletedKit, setSelectedDeletedKit] = useState<Kit>(formDefaultValue);
  const [selectedKitItemKey, setSelectedKitItemKey] = useState<string>("");
  const [backToProgram360, setbackToProgram360] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [isVGIInstallMethod, setIsVGIInstallMethod] =  useState<string>("");

  // Associate Program

  const selectedAssociatedPrograms = async (confirmOutput: ConfirmOutput) => {
    setAssociatedProgramDialogOpen(false);

    if (confirmOutput.status === "select") {
      const programKeys: string[] = [];
      confirmOutput.data.forEach((item) => {
        programKeys.push(item.value);
      });

      const payload = {
        programKeys: programKeys,
      };

      try {
        setReadonly(true);
        await API.post("/api/onboarding/kits/" + kitKey + "/programs", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully saved.", severity: "success" });
            setLoading(true);
            refreshData("kits");
            redirectRoutePage("onboarding/kits/" + kitKey);
            return;
          })
          .catch((error) => {
            setReadonly(false);
            showAlert({
              message: apiReponseError(error, "Failed to save program kits."),
              severity: "error",
            });
          });
      } catch (e) {
        setReadonly(false);
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  const handlAassociatedProgramClick = () => {
    const lookupValues: Lookup[] = [];

    for (let i = 0; i < referenceLookups.programs.length; i++) {
      if (kitAssociations.find((kitAssociation) => kitAssociation.programKey === referenceLookups.programs[i].programKey)?.programKey === undefined) {
        lookupValues.push({
          value: referenceLookups.programs[i].programKey,
          name: referenceLookups.programs[i].programName,
          checked: false,
        });
      }
    }
    setAssociatedProgramLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setAssociatedProgramDialogOpen(true);
  };

  // Clone Kit
  const openCloneKitConfirmDialog = () => {
    setCloneKitConfirmDialogInputOpen(true);
  };

  const saveCloneKit = async (confirmOutput: ConfirmOutput) => {
    setCloneKitConfirmDialogInputOpen(false);

    if (confirmOutput.status === "save") {
      if (!canWriteKit(userFunctionAccess)) {
        setCloneKitConfirmDialogInputOpen(false);
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("onboarding");
        return;
      }

      const payload = {
        name: confirmOutput.data,
      };

      try {
        setLoading(true);
        await API.post("/api/onboarding/kits/" + kitKey + "/clone", payload)
          .then(async (response) => {
            showAlert({ message: "Successfully clone.", severity: "success" });
            redirectRoutePage("onboarding/kits/" + response.data.kitKey);
            return;
          })
          .catch((error) => {
            setLoading(false);
            const errorList: ApiResponseErrorObject[] = getErrorObject(error, "kit.");
            let isErrorAssignedAtFieldLevel = true;
            try {
              errorList.forEach(function (error) {
                showAlert({
                  message: error.message.join(" "),
                  severity: "error",
                });
              });
            } catch (e) {
              isErrorAssignedAtFieldLevel = false;
            }

            if (isErrorAssignedAtFieldLevel) {
              setCloneKitConfirmDialogInputOpen(true);
              return;
            }

            if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
              showAlert({
                message: apiReponseError(error, "Failed to clone kit data."),
                severity: "error",
              });
            }
          });
      } catch (e) {
        showAlert({
          message: apiRequestError(e, "Something was wrong in our system."),
          severity: "error",
        });
        setLoading(false);
      }
    }
  };

  // Delete Kit
  const showDeleteConfirmationDialog = () => {
    setSelectedDeletedKit(formReference.current.values);
    setDeleteConfirmDialogOpenForKit(true);
  };

  const deleteConfirmationForKit = async (confirm: boolean) => {
    setDeleteConfirmDialogOpenForKit(false);
    if (confirm) {
      try {
        await API.delete("/api/onboarding/kits/" + kitKey)
          .then(async (response) => {
            showAlert({
              message: "Successfully removed.",
              severity: "success",
            });
            refreshData("kits");
            goToRoutePage("onboarding");
            return;
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to delete kit data."),
              severity: "error",
            });
          });
      } catch (e) {
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  // Save Kit

  const saveKit = async (values: Kit) => {
    if (!canWriteKit(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("onboarding");
      return;
    }

    const payload = {
      kit: {
        active: values.active,
        committmentPeriod: values.subscriptionBilling ? values.committmentPeriod : 0,
        fulfillmentVendorKey: values.fulfillmentVendorKey,
        kitKey: values.kitKey,
        name: values.name,
        platformVendorKey: values.platformVendorKey,
        replacementKit: values.replacementKit,
        subscriptionBilling: values.subscriptionBilling,
        defaultInstallMethodId: values.defaultInstallMethodId
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/kits/" + values.kitKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          refreshData("kits");
          redirectRoutePage("onboarding/kits/" + response.data.kitKey);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "kit.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }

          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save kit data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something was wrong in our system."),
        severity: "error",
      });
    }
  };

  // Kit Item
  const newKitItem = () => {
    setSelectedKitItemKey("");
    setIsSubscriptionBilling(formReference.current.values.subscriptionBilling);
    setKitItemDialogOpen(true);
  };

  const editKitItem = (kitItem: KitItem) => {
    if (saveButtonVisible) {
      return;
    }
   
    setSelectedKitItemKey(kitItem.kitItemKey);
    setIsSubscriptionBilling(formReference.current.values.subscriptionBilling);
    setIsVGIInstallMethod(formReference.current.values.defaultInstallMethodId);
    setKitItemDialogOpen(true);
  };

  const saveKitItemStatus = async (confirmOutput: ConfirmOutput) => {
    setKitItemDialogOpen(false);

    if (confirmOutput.status === "cancel") {
      if (backToProgram360) {
        setOnboardingPagePathName("Program 360");
        goToRoutePage("onboarding");
        return;
      }
      return;
    }

    if (confirmOutput.status === "refresh") {
      refreshData("kits");

      if (backToProgram360) {
        setOnboardingPagePathName("Program 360");
        goToRoutePage("onboarding");
        return;
      }

      setLoading(true);
      redirectRoutePage("onboarding/kits/" + kitKey);
    }
  };

  // Other

  const enableActive = () => {
    if (userFunctionAccess.onboardingKitApprover) {
      return true;
    }

    if (!readonly && !limitedUpdates) {
      return true;
    }

    return false;
  };

  const gotoProgram = (kitAssociation: KitAssociation) => {
    if (!canReadProgram(userFunctionAccess)) {
      showAlert({ message: "Access Denied", severity: "error" });
      return;
    }
    goToRoutePage("onboarding/programs/" + kitAssociation.programKey);
  };

  const cancel = () => {
    if (backToProgram360) {
      setOnboardingPagePathName("Program 360");
      goToRoutePage("onboarding");
      return;
    }

    goToRoutePage("onboarding/kits");
  };

  useEffect(() => {
    setWindowTitle("Onboarding - Kit");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (isPageInitialize) {
        return;
      }

      setReadonly(true);
      if (!canReadKit(userFunctionAccess)) {
        showAlert({ message: "Access Denied", severity: "error" });
        goToRoutePage("home");
        return;
      }

      if (isNullOrWhiteSpace(kitKey)) {
        showAlert({ message: "Kit key missing.", severity: "error" });
        goToRoutePage("onboarding");
        return;
      }

      if (kitKey === referenceLookups.newRecordDefaultId) {
        if (!canWriteKit(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          goToRoutePage("onboarding");
          return;
        }

        let defaultVendorKey = "";
        const filterVendors = referenceLookups.vendors.filter((vendor) => vendor.name === "Meshify UI");
        if (filterVendors.length === 1) {
          defaultVendorKey = filterVendors[0].vendorKey;
        }

        let defaultFulfillmentVendorKey = "";
        const filterFulfillmentVendors = referenceLookups.vendors.filter((vendor) => vendor.name === "Shopify");
        if (filterFulfillmentVendors.length === 1) {
          defaultFulfillmentVendorKey = filterFulfillmentVendors[0].vendorKey;
        }

        setInitialFormValues({
          ...formDefaultValue,
          kitKey: referenceLookups.newRecordDefaultId,
          platformVendorKey: defaultVendorKey,
          fulfillmentVendorKey: defaultFulfillmentVendorKey,
        });
        setReadonly(false);
        setIsNew(true);
        setLoading(false);
        setIsPageInitialize(true);
        return;
      }

      const apiResponse = await getKitDetail(kitKey);

      if (apiResponse.success) {
        setInitialFormValues(apiResponse.data.kit);
        setKitAssociations(apiResponse.data.associations);
        setKitItems(apiResponse.data.kitItems);
      
        setReadonly(!canWriteKit(userFunctionAccess));
        setIsNew(false);
        setIsPageInitialize(true);
        setLimitedUpdates(false);

        // if (apiResponse.data.kit.enrollmentCount > 0) {
        //   setLimitedUpdates(true);
        // }

        setLoading(false);
        setIsSubscriptionBilling(apiResponse.data.kit.subscriptionBilling);
           setIsVGIInstallMethod(apiResponse.data.kit.defaultInstallMethodId as string);
        if (kitItemKey !== "" && kitItemKey !== undefined) {
          setSelectedKitItemKey(kitItemKey);
          setIsSubscriptionBilling(apiResponse.data.kit.subscriptionBilling);
          setIsVGIInstallMethod(apiResponse.data.kit.defaultInstallMethodId as string);
          setKitItemDialogOpen(true);
          setbackToProgram360(true);
        }
      } else {
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
        goToRoutePage("onboarding");
        return;
      }
      
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitKey, referenceLookups, userFunctionAccess]);

  return (
    <>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <SaveBackdrop open={saveInProgress} />

          <KitItemCRUD open={kitItemDialogOpen} isSubscriptionBilling={isSubscriptionBilling} kitKey={kitKey} kitItemKey={selectedKitItemKey} onCallback={saveKitItemStatus} />

          <ConfirmSelect title="Select Programs To Associate" label={"Kit"} content={``} data={associatedProgramLookupValues} yesText="ADD" noText="CANCEL" open={associatedProgramDialogOpen} multiSelect={true} maxSelect={0} callbackOnSelect={selectedAssociatedPrograms} />

          <Confirm title="Confirmation" content={`<p>Are you sure you want to remove this kit?</p><p style="text-align:center"><b>${selectedDeletedKit.hsbKitNo}</b></p><p style="text-align:center"><b>${selectedDeletedKit.name}</b></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpenForKit} callbackOnClose={() => { }} callbackOnConfirm={deleteConfirmationForKit} />

          <ConfirmInput title="New Kit Name" label="Name" content={""} value={""} type={"string"} required={true} yesText="SAVE" noText="CANCEL" open={cloneKitConfirmDialogInputOpen} callbackOnSave={saveCloneKit} deleteConfirmationText={""} showDeleteButton={false} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box className={globalStyles.bottomSpacer}>
                <Breadcrumbs aria-label="breadcrumb" maxItems={getBreadcrumbMaxItems(4)}>
                  <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                    Home
                  </Link>
                  <Link color="inherit" onClick={() => goToRoutePage("onboarding")} className={globalStyles.link}>
                    Onboarding
                  </Link>
                  <Link color="inherit" onClick={() => goToRoutePage("onboarding\\kits")} className={globalStyles.link}>
                    Kit
                  </Link>
                  <Typography color="textPrimary">{isNew ? "New" : readonly ? "View" : "Edit"}</Typography>
                </Breadcrumbs>
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={8} lg={9} className={globalStyles.formikContainer}>
              <Paper elevation={0} className={pageStyles.inputPaperContainer}>
                <Grid container>
                  {limitedUpdates ? (
                    <>
                      <Grid item xs={12}>
                        <Alert severity="info">{"Please note that limited updates can be made to this kit since " + initialFormValues.enrollmentCount + " participants have already been enrolled."}</Alert>
                      </Grid>
                      <Grid item xs={12}>
                        <Box p={3}></Box>
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>
                  )}

                  <Grid item xs={12} className={pageStyles.formContainer}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Formik
                          innerRef={formReference}
                          initialValues={initialFormValues}
                          validationSchema={formValidationSchema}
                          validateOnMount={true}
                          initialTouched={formDefaultTouchValue}
                          validateOnBlur={true}
                          validateOnChange={true}
                          onSubmit={(values: Kit) => {
                            saveKit(values);
                          }}
                        >
                          {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue }) => (
                            <Form
                              onChange={() => {
                                setSaveButtonVisible(true);
                              }}
                            >
                              <Grid container spacing={3}>
                                <Grid item xs={3} sm={4} md={3} lg={1}>
                                  <TextField disabled={true} id="hsbKitNo" label="Kit No" type="number" value={values.hsbKitNo} onChange={handleChange} onBlur={handleBlur} fullWidth={false} />
                                </Grid>

                                <Grid item xs={9} sm={8} md={9} lg={5}>
                                  <TextField  id="name" label="Name" value={values.name} onChange={handleChange} onBlur={handleBlur} error={touched.name && Boolean(errors.name)} helperText={errors.name && touched.name ? errors.name : null} autoFocus={isNew} />
                                </Grid>
                            <Grid item xs={6} sm={6} md={6} lg={3}>
                            <FormControl
                                                                    className={globalStyles.formControl}
                                                                    fullWidth={true} error={Boolean(errors.defaultInstallMethodId)}
                                                                    
                                                                  >
                                                                    <InputLabel id="defaultInstallMethodId-label" shrink={true} >
                                                                      Install Option
                                                                    </InputLabel>
                                                                    <Select
                                                                      disabled={readonly}
                                                                      native
                                                                      labelId="defaultInstallMethodId-label"
                                                                      id="defaultInstallMethodId"
                                                                      name="defaultInstallMethodId"
                                                                      value={values.defaultInstallMethodId}
                                                                     
                                                                     onChange={(e) => {
                        if (e !== null) {
                          setIsVGIInstallMethod(e.target.value as string);
                          handleChange(e);
                        }

                      }}                                                                  
                                                                      error={Boolean(getIn(errors, "values.defaultInstallMethodId"))}
                                                                    >
                                                                      <option value={" "}></option>
                                                                      <option value={"1"}>PRO</option>
                                                                      <option value={"2"}>Self</option>
                                                                      <option value={"3"}>VGI</option>
                                                                    </Select>
                                                                    <FormHelperText error>{errors.defaultInstallMethodId}</FormHelperText>
                                                                  </FormControl>
                          </Grid>
                                <Grid item xs={6} sm={6} md={6} lg={3}>
                                  <FormControl disabled={readonly || limitedUpdates} className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.fulfillmentVendorKey)}>
                                    <InputLabel id="fulfillment-vendorI-id-label" shrink={true}>
                                      Fulfillment Vendor
                                    </InputLabel>
                                    <Select native labelId="fulfillment-vendorI-id-label" id="fulfillmentVendorKey" name="fulfillmentVendorKey" value={values.fulfillmentVendorKey} onChange={handleChange} label="Fulfillment Vendor" error={touched.fulfillmentVendorKey && Boolean(errors.fulfillmentVendorKey)}>
                                      {referenceLookups.vendors.map((vendor, index) => (
                                        <option value={vendor.vendorKey} key={index}>
                                          {vendor.name}
                                        </option>
                                      ))}
                                    </Select>
                                    <FormHelperText error>{errors.fulfillmentVendorKey}</FormHelperText>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={6} sm={6} md={6} lg={3}>
                                  <FormControl disabled={readonly || limitedUpdates} className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.platformVendorKey)}>
                                    <InputLabel id="platform-vendorI-id-label" shrink={true}>
                                      Platform Vendor
                                    </InputLabel>
                                    <Select native labelId="platform-vendor-id-label" id="platformVendorKey" name="platformVendorKey" value={values.platformVendorKey} onChange={handleChange} label="Platform Vendor" error={touched.platformVendorKey && Boolean(errors.platformVendorKey)}>
                                      {referenceLookups.vendors.map((vendor, index) => (
                                        <option value={vendor.vendorKey} key={index}>
                                          {vendor.name}
                                        </option>
                                      ))}
                                    </Select>
                                    <FormHelperText error>{errors.platformVendorKey}</FormHelperText>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={6} sm={3} md={3} lg={3}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                                    <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                      Active
                                    </FormLabel>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            //disabled={readonly || !userFunctionAccess.onboardingKitApprover}
                                            disabled={!enableActive()}
                                            id="active"
                                            name="active"
                                            checked={values.active}
                                            onChange={(ev, checked) => {
                                              setFieldValue("active", checked);
                                            }}
                                          />
                                        }
                                        label={values.active ? "Yes" : "No"}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={6} sm={3} md={3} lg={3}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                                    <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                      Replacement Kit
                                    </FormLabel>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            disabled={readonly || limitedUpdates}
                                            id="replacementKit"
                                            name="replacementKit"
                                            checked={values.replacementKit}
                                            onChange={(ev, checked) => {
                                              setFieldValue("replacementKit", checked);
                                            }}
                                          />
                                        }
                                        label={values.replacementKit ? "Yes" : "No"}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={6} sm={3} md={3} lg={3}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                                    <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                      Subscription Billing
                                    </FormLabel>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            disabled={readonly || limitedUpdates}
                                            id="subscriptionBilling"
                                            name="subscriptionBilling"
                                            checked={values.subscriptionBilling}
                                            onChange={(ev, checked) => {
                                              setFieldValue("subscriptionBilling", checked);
                                              setIsSubscriptionBilling(checked);
                                            }}
                                          />
                                        }
                                        label={values.subscriptionBilling ? "Yes" : "No"}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>

                                {values.subscriptionBilling ? (
                                  <Grid item xs={6} sm={3} md={3} lg={3}>
                                    <TextField disabled={readonly || limitedUpdates} id="committmentPeriod" label="Committment Period" type="number" value={values.committmentPeriod} onChange={handleChange} onBlur={handleBlur} fullWidth={false} error={touched.committmentPeriod && Boolean(errors.committmentPeriod)} helperText={errors.committmentPeriod && touched.committmentPeriod ? errors.committmentPeriod : null} />
                                  </Grid>
                                ) : null}

                                <Grid item xs={12}>
                                  <Box p={1} pt={3} display="flex" justifyContent="center">
                                    {!readonly && !limitedUpdates && initialFormValues.hsbKitNo > 0 && kitKey !== referenceLookups.newRecordDefaultId ? (
                                      <>
                                        {kitAssociations.length === 0 ? (
                                          <Button onClick={() => showDeleteConfirmationDialog()} className={globalStyles.errorText}>
                                            <DeleteIcon />
                                          </Button>
                                        ) : null}
                                      </>
                                    ) : null}

                                    {!readonly && !saveButtonVisible && initialFormValues.hsbKitNo > 0 && kitKey !== referenceLookups.newRecordDefaultId && userFunctionAccess.onboardingKitAdminWrite ? (
                                      <Button
                                        id="button-clone"
                                        color="default"
                                        variant="contained"
                                        className={globalStyles.buttonDefault}
                                        onClick={() => {
                                          openCloneKitConfirmDialog();
                                        }}
                                      >
                                        Clone
                                      </Button>
                                    ) : null}
                                    {!readonly ? (
                                      <Button disabled={!saveButtonVisible} id="button-save" variant="contained" color="secondary" onClick={() => formReference.current.handleSubmit()} className={globalStyles.buttonDefault}>
                                        {isNew ? "Save" : "Update"}
                                      </Button>
                                    ) : null}
                                    <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => cancel()}>
                                      <u>{!readonly ? "Cancel" : "Back"}</u>
                                    </Button>
                                  </Box>
                                  <Box p={1}></Box>
                                </Grid>
                              </Grid>
                            </Form>
                          )}
                        </Formik>

                        <Grid container spacing={3}>
                          {!isNew && initialFormValues.hsbKitNo > 0 ? (
                            <>
                              <Grid item xs={12}>
                                <Box className={globalStyles.topic}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={8}>
                                      Associated Items                                       
                                    </Grid> 
                                    
                                    <Grid item xs={4}>
                                      <Box display="flex" justifyContent="flex-end">
                                        <Box display="flex" justifyContent="flex-end" width={1}>
                                          {/* && !limitedUpdates && (initialFormValues.active || initialFormValues.enrollmentCount === 0)  */}
                                          {!readonly && !saveButtonVisible ? (
                                            <Link href="#" onClick={() => newKitItem()}>
                                              <AddIcon color="secondary" />
                                            </Link>
                                          ) : null}
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                              { (kitItems.filter((item) => item.partNumber && item.partNumber !== "TK-KONA-WT2" && item.partNumber !== "TK-KONA-WT1" && item.partNumber !== "KRGTWYM1")?.length )>7 && isVGIInstallMethod.toString() ==='3' ? (
                                <Grid item xs={12}>
                                  <Alert severity="warning" > The Install Option select for this kit is VGI, you have added more than 7 devices to the kit. </Alert>
                                </Grid>
                              ) :null}
                              {isSubscriptionBilling && kitItems.filter((item) => item.rollupItem)?.length === 0 ? (
                                <Grid item xs={12}>
                                  <Alert severity="error">A Rollup item is required with Subscription Billing</Alert>
                                </Grid>
                              ) : null}

                              <Grid item xs={12}>
                                {kitItems.length > 0 ? <KitItemList kitItems={kitItems} onCallback={editKitItem} /> : null}
                              </Grid>
                            </>
                          ) : null}
                          <Grid item xs={12}>
                            <Box p={1}></Box>
                          </Grid>
                        </Grid>
                        <Box p="3">&nbsp;</Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Box p={1}></Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={12} md={4} lg={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={12} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader title="" subheader="INCLUDES" />
                    <CardContent>
                      <Grid container>
                        <Grid item xs={6}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="count"  >
                                {kitAssociations.length}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Programs
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={6}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="count"  >
                                {kitItems.length}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Total Items
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>



                        {isSubscriptionBilling ? (
                          <>
                            <Grid item xs={6}  >
                              <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                <Box>
                                  <Typography variant="h6" className="count"  >
                                    {kitItems.length > 0
                                      ? kitItems
                                        .map(function (kitItem) {
                                          return kitItem.quantity;
                                        })
                                        .reduce(function (totalQuantity, quantity) {
                                          return totalQuantity + quantity;
                                        })
                                      : 0}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                    Total Quantity
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={6}  >
                              <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                <Box>
                                  <Typography variant="h6" className="count"  >
                                    $
                                    {kitItems.length > 0
                                      ? kitItems
                                        .map(function (kitItem) {
                                          return kitItem.price;
                                        })
                                        .reduce(function (totalPrice, price) {
                                          return totalPrice + price;
                                        })
                                      : 0}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                    Price
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={12}  >
                              <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                <Box>
                                  <Typography variant="h6" className="count"  >
                                    {kitItems.length > 0
                                      ? kitItems
                                        .map(function (kitItem) {
                                          return kitItem.quantity;
                                        })
                                        .reduce(function (totalQuantity, quantity) {
                                          return totalQuantity + quantity;
                                        })
                                      : 0}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                    Total Quantity
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </>
                        )}

                      </Grid>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                </Grid>
                <Grid item xs={12} sm={6} md={12} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader
                      title=""
                      subheader="ASSOCIATED PROGRAMS"
                      action={
                        <>
                          {!isNew && !saveButtonVisible && canWriteProgram(userFunctionAccess) && initialFormValues.active ? (
                            <Tooltip arrow title={"Associated Programs"}>
                              <IconButton onClick={() => handlAassociatedProgramClick()}>
                                <LinkIcon color={"secondary"} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </>
                      }
                    />
                    <CardContent>
                      <Box className={pageStyles.kitAssociationContent}>
                        <List>
                          {kitAssociations.length > 0
                            ? kitAssociations.map((kitAssociation, index) => (
                              <React.Fragment key={index}>
                                {canReadProgram(userFunctionAccess) ? (
                                  <ListItem button onClick={() => gotoProgram(kitAssociation)}>
                                    <ListItemText primary={kitAssociation.programName} secondary={kitAssociation.companyName} />
                                    <ListItemSecondaryAction>
                                      <IconButton edge="end" aria-label="delete">
                                        <ChevronRightIcon />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ) : (
                                  <ListItem>
                                    <ListItemText primary={kitAssociation.programName} secondary={kitAssociation.companyName} />
                                  </ListItem>
                                )}
                              </React.Fragment>
                            ))
                            : null}
                        </List>
                      </Box>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                </Grid>

                <Grid item xs={12} sm={6} md={12} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader title="" subheader="AUDIT" />
                    <CardContent>
                      <Grid container>
                        <Grid item xs={12}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="text"  >
                                {!isNew && initialFormValues.lastModifiedBy !== "" ? initialFormValues.lastModifiedBy : "  "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Last Update By
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}  >&nbsp;</Grid>
                        <Grid item xs={12}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="text"  >
                                {!isNew && initialFormValues.lastModifiedDateTime !== "" ? <Moment date={initialFormValues.lastModifiedDateTime} format="M/D/YYYY - h:mm:ss A z" /> : "  "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Last Updated On
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                      </Grid>
                    </CardContent>
                  </Card>

                  <Box p="3">&nbsp;</Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Screen>
      )}
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      maxWidth: "1200px",
    },
    inputPaperContainer: {
      padding: theme.spacing(0),
      backgroundColor: "transparent",
    },
    countContainer: {
      justifyItems: "center",
    },
    kitAssociationContent: {
      maxHeight: "250px",
      overflow: "auto",
    },
  })
);

export default KitCRUD;
